/**
 * Campaign interface that UI components expect
 * Maps from Django API response to legacy structure
 */

import type { PublicCampaignResponse } from './types/api';

// Legacy Campaign interface - kept for UI compatibility
export interface Campaign {
  id: string;
  user_phone_number: string;
  user_name: string;
  description: string;
  title: string;
  product: Product;
  user_avatar: string;
  userId: string;
  createdAt: string;
  amount_raised: number;
  totalContributions: number;
  // Campaign state (used for gating donate flow)
  status?: string;
  is_active?: boolean;
  expires_at?: string | null;
  user_data?: {
    amount: number;
    payer_name: string;
    share_url: string;
  };
  url?: string;
  // Django API fields (new)
  progress?: {
    target_amount: string;
    total_contributed: string;
    remaining_balance: string;
    progress_percentage: number;
    contributor_count: number;
  };
  social_sharing?: {
    direct: string;
    whatsapp: string;
    sms: string;
    facebook: string;
    twitter: string;
  };
  campaign_rules?: {
    min_contribution: string;
    max_contribution: string;
  };

  recent_contributions?: Array<{
    contributor_name: string;
    amount: string;
    message: string;
    contributed_at: string;
    is_anonymous: boolean;
  }>;
}

export interface Product {
  retail_price: number;
  name: string;
  description: string;
  image_url: {
    url: string;
    url_x24?: string;
  };
  // Legacy fields - optional for backwards compatibility
  merchant_shop_id?: string;
  brand_name?: string;
  search_brand_name?: string;
  plans?: Plan[];
  out_of_stock?: boolean;
  display?: boolean;
  model_no?: string;
  alt_image_url?: Altimageurl[];
  categories?: string[];
  is_featured?: boolean;
  search_name?: string;
  cost_price?: number;
  status?: string;
}

export interface Altimageurl {
  url_x24?: string;
  url: string;
}

export interface Plan {
  '3'?: number;
  '2'?: number;
  '1'?: number;
}

/**
 * Maps Django API response to the legacy Campaign interface
 * Used by page.tsx and other components
 */
export function mapApiResponseToCampaign(response: PublicCampaignResponse): Campaign {
  const orderContext = response.order_context;
  const fundraiser = response.fundraiser;
  const progress = response.progress;

  return {
    id: response.campaign_id,
    user_phone_number: fundraiser?.phone || fundraiser?.masked_phone || '',
    user_name: fundraiser?.name || 'Anonymous',
    description: response.description || '',
    title: response.title || orderContext.product_name || '',
    product: {
      retail_price: parseFloat(orderContext.total_amount) || 0,
      name: orderContext.product_name || '',
      description: '',
      image_url: {
        url: orderContext.product_image_url || '',
      },
    },
    // Default avatar since Django doesn't return avatar URL
    user_avatar: '',
    userId: '',
    createdAt: response.created_at,
    amount_raised: parseFloat(progress.total_contributed) || 0,
    totalContributions: progress.contributor_count || 0,
    status: response.status,
    is_active: response.is_active,
    expires_at: response.expires_at,
    url: response.social_sharing?.direct,
    progress: {
      target_amount: progress.target_amount,
      total_contributed: progress.total_contributed,
      remaining_balance: progress.remaining_balance,
      progress_percentage: parseFloat(progress.progress_percentage) || 0,
      contributor_count: progress.contributor_count,
    },
    social_sharing: response.social_sharing || undefined,
    campaign_rules: {
      min_contribution: response.campaign_rules.min_contribution,
      max_contribution: response.campaign_rules.max_contribution,
    },
    recent_contributions: response.recent_contributions,
  };
}
